import React, { useState, useEffect, useCallback } from 'react';
import { fetchKillSwitch, updateKillSwitch, resetKillSwitch } from '../api';

const POLL_MS = 5000;

function fmtINR(n) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  const sign = v < 0 ? '-' : '';
  return `${sign}₹${Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', { hour12: false });
  } catch {
    return iso;
  }
}

export default function KillSwitchPanel() {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetchKillSwitch();
      const list = res?.data?.states;
      if (Array.isArray(list)) {
        setStates(list);
      } else if (res?.data?.state) {
        // Backwards-compat if the server ever falls back to single-state.
        setStates([res.data.state]);
      } else {
        setStates([]);
      }
    } catch {
      setStates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  if (loading && states.length === 0) {
    return (
      <div className="card" style={{ padding: 16, marginTop: 16 }}>
        <div className="card-title" style={{ marginBottom: 8 }}>
          Account Daily-Loss Kill Switch
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>
      </div>
    );
  }

  if (states.length === 0) {
    return (
      <div className="card" style={{ padding: 16, marginTop: 16 }}>
        <div className="card-title" style={{ marginBottom: 8 }}>
          Account Daily-Loss Kill Switch
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          No active Dhan broker accounts configured. Add one in the Accounts
          settings above to enable the per-account kill switch.
        </div>
      </div>
    );
  }

  return (
    <>
      {states.map((s) => (
        <KillSwitchCard
          key={`ks-${s.account_id}`}
          state={s}
          onSaved={load}
        />
      ))}
    </>
  );
}

function KillSwitchCard({ state, onSaved }) {
  const [limitInput, setLimitInput] = useState(String(state?.limit ?? ''));
  const [enabledInput, setEnabledInput] = useState(Boolean(state?.enabled));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [msg, setMsg] = useState(null);

  // Keep local form state in sync with server state when the parent
  // re-polls, but ONLY when the user isn't mid-edit — otherwise we'd
  // stomp on their in-progress typing every 5 seconds.
  useEffect(() => {
    if (dirty) return;
    setLimitInput(String(state?.limit ?? ''));
    setEnabledInput(Boolean(state?.enabled));
  }, [state?.limit, state?.enabled, dirty]);

  const accountId = Number(state?.account_id ?? 0);
  const accountName = state?.account_name || `account-${accountId}`;
  const brokerType = (state?.broker || 'dhan').toUpperCase();
  const enabled = Boolean(state?.enabled);
  const locked = Boolean(state?.locked);
  const pnl = Number(state?.current_pnl ?? 0);
  const limit = Number(state?.limit ?? 0);
  const usedPct = limit > 0 && pnl < 0 ? Math.min(100, (Math.abs(pnl) / limit) * 100) : 0;

  // Save should light up whenever the on-screen values differ from what
  // the server currently holds — not only when the user has typed since
  // mount. Relying solely on a `dirty` flag makes the button appear
  // permanently grey when the initial input value already matches
  // (e.g. after a page reload while TRIPPED), so users think it's
  // unclickable.
  const parsedLimit = parseFloat(limitInput);
  const validLimit = Number.isFinite(parsedLimit) && parsedLimit > 0;
  const hasChanges =
    (validLimit && parsedLimit !== Number(state?.limit ?? 0)) ||
    Boolean(enabledInput) !== Boolean(state?.enabled);
  const canSave = validLimit && !saving && (hasChanges || dirty);

  const barColor = !enabled
    ? 'var(--text-muted)'
    : locked
      ? 'var(--accent-red)'
      : usedPct > 75
        ? '#f59e0b'
        : 'var(--accent-green)';

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const limitNum = parseFloat(limitInput);
      if (!Number.isFinite(limitNum) || limitNum <= 0) {
        setMsg({ ok: false, text: 'Limit must be a positive number.' });
        setSaving(false);
        return;
      }
      const res = await updateKillSwitch({
        account_id: accountId,
        enabled: enabledInput,
        limit: limitNum,
      });
      // The backend returns { ok, state, error? } — treat an explicit
      // ok:false OR a missing state as failure and surface the exact
      // error so users aren't left guessing.
      const okFlag = res?.data?.ok;
      const s = res?.data?.state;
      const errText = res?.data?.error;
      if (okFlag === false || (!s && errText)) {
        setMsg({ ok: false, text: errText || 'Update failed.' });
      } else if (s) {
        setLimitInput(String(s.limit ?? ''));
        setEnabledInput(Boolean(s.enabled));
        setDirty(false);

        const parts = [`Saved (limit ₹${Number(s.limit).toLocaleString('en-IN')}, ${s.enabled ? 'enabled' : 'disabled'}).`];
        if (s.locked) {
          parts.push('Switch is still TRIPPED — click “Reset Kill Switch” to allow new entries.');
        } else {
          parts.push('New limit applies on the next watchdog tick.');
        }
        setMsg({ ok: true, text: parts.join(' ') });
        if (typeof onSaved === 'function') onSaved();
      } else {
        setMsg({ ok: false, text: errText || 'Update failed (no state returned).' });
      }
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.message ||
        'Update failed.';
      setMsg({ ok: false, text: `Update failed: ${detail}` });
    } finally {
      setSaving(false);
    }
  };

  const doReset = async () => {
    if (!window.confirm(`Reset the kill switch for ${accountName}? Bot entries on this account will resume for today.`)) return;
    setResetting(true);
    setMsg(null);
    try {
      const res = await resetKillSwitch({
        account_id: accountId,
        reason: 'manual_reset_via_ui',
      });
      const s = res?.data?.state;
      if (s) {
        setMsg({ ok: true, text: 'Kill switch cleared. Watch for re-trip on next loss.' });
        if (typeof onSaved === 'function') onSaved();
      }
    } catch (e) {
      setMsg({ ok: false, text: e?.message || 'Reset failed.' });
    } finally {
      setResetting(false);
    }
  };

  return (
    <div
      className="card"
      style={{
        padding: 16,
        marginTop: 16,
        borderLeft: `3px solid ${locked ? 'var(--accent-red)' : 'var(--accent-blue)'}`,
      }}
    >
      <div
        className="card-title"
        style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
      >
        <span>Kill Switch — {accountName}</span>
        <span
          style={{
            fontSize: 10,
            padding: '2px 8px',
            borderRadius: 4,
            background: 'rgba(148,163,184,0.15)',
            color: 'var(--text-secondary)',
            fontWeight: 600,
            letterSpacing: 0.4,
          }}
        >
          {brokerType}
        </span>
        {!enabled && (
          <span
            style={{
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 4,
              background: 'rgba(148,163,184,0.18)',
              color: 'var(--text-muted)',
              fontWeight: 600,
              letterSpacing: 0.4,
            }}
          >
            DISABLED
          </span>
        )}
        {locked && (
          <span
            style={{
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 4,
              background: 'rgba(239,68,68,0.15)',
              color: 'var(--accent-red)',
              fontWeight: 600,
              letterSpacing: 0.4,
            }}
          >
            TRIPPED
          </span>
        )}
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.6 }}>
        Polls this account's Dhan positions every few seconds and computes total realised + unrealised PnL
        across <strong>every</strong> position (including manually-placed ones).
        When the loss limit is breached, only this account's positions are force-closed and further
        <em> new entries</em> on this account are blocked until midnight IST. Other accounts continue trading.
      </div>

      {/* Live PnL bar */}
      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 12,
            color: 'var(--text-secondary)',
            marginBottom: 4,
          }}
        >
          <span>
            Current PnL:&nbsp;
            <strong style={{ color: pnl < 0 ? 'var(--accent-red)' : 'var(--accent-green)' }}>
              {fmtINR(pnl)}
            </strong>
          </span>
          <span>Limit: {fmtINR(-limit)}</span>
        </div>
        <div
          style={{
            height: 8,
            borderRadius: 4,
            background: 'rgba(255,255,255,0.06)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${usedPct}%`,
              height: '100%',
              background: barColor,
              transition: 'width 0.4s ease',
            }}
          />
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          {usedPct.toFixed(0)}% of loss budget used · last poll {fmtTime(state?.last_poll_at)}
        </div>
      </div>

      {/* Settings form */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          gap: 12,
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={enabledInput}
            onChange={(e) => {
              setEnabledInput(e.target.checked);
              setDirty(true);
            }}
          />
          Enabled
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          Daily loss limit (₹):
          <input
            type="number"
            min="100"
            step="100"
            value={limitInput}
            onChange={(e) => {
              setLimitInput(e.target.value);
              setDirty(true);
            }}
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(0,0,0,0.2)',
              color: 'var(--text-primary)',
              width: 140,
              fontSize: 13,
            }}
          />
        </label>

        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          title={
            !validLimit
              ? 'Enter a positive limit first'
              : hasChanges
                ? 'Save changes to this account'
                : 'No changes — modify a value first'
          }
          style={{
            padding: '7px 16px',
            borderRadius: 6,
            border: 'none',
            cursor: !canSave ? 'not-allowed' : 'pointer',
            background: 'rgba(59,130,246,0.15)',
            color: 'var(--accent-blue)',
            fontSize: 13,
            fontWeight: 600,
            opacity: !canSave ? 0.5 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* Reset button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={doReset}
          disabled={!locked || resetting}
          style={{
            padding: '7px 16px',
            borderRadius: 6,
            border: 'none',
            cursor: !locked || resetting ? 'not-allowed' : 'pointer',
            background: locked ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.05)',
            color: locked ? 'var(--accent-red)' : 'var(--text-muted)',
            fontSize: 13,
            fontWeight: 600,
            opacity: !locked || resetting ? 0.5 : 1,
          }}
        >
          {resetting ? 'Resetting…' : 'Reset Kill Switch'}
        </button>
        {state?.tripped_at && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Tripped at {fmtTime(state.tripped_at)} @ PnL {fmtINR(state.tripped_pnl)}
          </span>
        )}
      </div>

      {msg && (
        <div
          style={{
            marginTop: 12,
            padding: '8px 12px',
            borderRadius: 6,
            background: msg.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${msg.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            fontSize: 12,
            color: msg.ok ? 'var(--accent-green)' : 'var(--accent-red)',
          }}
        >
          {msg.text}
        </div>
      )}

      {state?.last_error && (
        <div
          style={{
            marginTop: 8,
            fontSize: 11,
            color: 'var(--accent-red)',
            opacity: 0.85,
          }}
        >
          Last watchdog error: {state.last_error}
        </div>
      )}
    </div>
  );
}
